import { unzipSync } from 'fflate';
import { Element, parseXmlBytes } from './xml.js';

/**
 * Open Packaging Conventions: the zip container, the content-type map and the
 * relationship graph that turns `r:embed="rId3"` into an actual part.
 */

export interface Relationship {
  readonly id: string;
  readonly type: string;
  readonly target: string;
  /** External relationships point outside the package — linked media, hyperlinks. */
  readonly external: boolean;
  /** Absolute part name inside the package. Undefined for external targets. */
  readonly resolvedPart?: string;
}

export const REL = {
  slide: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide',
  slideLayout: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/slideLayout',
  slideMaster: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/slideMaster',
  notesSlide: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/notesSlide',
  theme: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme',
  image: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/image',
  audio: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/audio',
  video: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/video',
  media: 'http://schemas.microsoft.com/office/2007/relationships/media',
  hyperlink: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink',
  officeDocument:
    'http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument',
  chart: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart',
  diagramDrawing: 'http://schemas.microsoft.com/office/2007/relationships/diagramDrawing',
  oleObject: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/oleObject',
} as const;

export class OpcPackage {
  readonly #parts: Map<string, Uint8Array>;
  readonly #xmlCache = new Map<string, Element>();
  readonly #relCache = new Map<string, Map<string, Relationship>>();
  readonly #contentTypes: { defaults: Map<string, string>; overrides: Map<string, string> };

  private constructor(parts: Map<string, Uint8Array>) {
    this.#parts = parts;
    this.#contentTypes = this.#readContentTypes();
  }

  /** Unzips a .pptx byte array. Throws only when the bytes are not a zip at all. */
  static open(bytes: Uint8Array): OpcPackage {
    const files = unzipSync(bytes);
    const parts = new Map<string, Uint8Array>();
    for (const [name, data] of Object.entries(files)) {
      // Zip entries have no leading slash; part names conventionally do.
      parts.set(normalisePart(`/${name}`), data);
    }
    return new OpcPackage(parts);
  }

  get partNames(): string[] {
    return [...this.#parts.keys()].sort();
  }

  get partCount(): number {
    return this.#parts.size;
  }

  has(partName: string): boolean {
    return this.#parts.has(normalisePart(partName));
  }

  bytes(partName: string): Uint8Array | undefined {
    return this.#parts.get(normalisePart(partName));
  }

  /** Parsed XML for a part, cached. Missing parts yield an empty element. */
  xml(partName: string): Element {
    const name = normalisePart(partName);
    const cached = this.#xmlCache.get(name);
    if (cached) return cached;
    const bytes = this.#parts.get(name);
    const el = bytes ? parseXmlBytes(bytes) : new Element('#missing', {}, [], '');
    this.#xmlCache.set(name, el);
    return el;
  }

  contentType(partName: string): string {
    const name = normalisePart(partName);
    const override = this.#contentTypes.overrides.get(name);
    if (override) return override;
    const ext = extensionOf(name);
    return this.#contentTypes.defaults.get(ext) ?? 'application/octet-stream';
  }

  /** Relationships declared by a part, keyed by rId. */
  relationships(partName: string): Map<string, Relationship> {
    const source = normalisePart(partName);
    const cached = this.#relCache.get(source);
    if (cached) return cached;

    const relPart = relsPartFor(source);
    const map = new Map<string, Relationship>();
    const root = this.xml(relPart);
    for (const rel of root.all('Relationship')) {
      const id = rel.attr('Id');
      const type = rel.attr('Type');
      const target = rel.attr('Target');
      if (!id || !type || target === undefined) continue;
      const external = rel.attr('TargetMode') === 'External';
      map.set(id, {
        id,
        type,
        target,
        external,
        ...(external ? {} : { resolvedPart: resolveRelative(source, target) }),
      });
    }
    this.#relCache.set(source, map);
    return map;
  }

  relationship(partName: string, id: string): Relationship | undefined {
    return this.relationships(partName).get(id);
  }

  /** Part name a relationship id resolves to, or undefined for external targets. */
  resolve(partName: string, id: string | undefined): string | undefined {
    if (!id) return undefined;
    return this.relationship(partName, id)?.resolvedPart;
  }

  relationshipsOfType(partName: string, type: string): Relationship[] {
    return [...this.relationships(partName).values()].filter((r) => r.type === type);
  }

  #readContentTypes(): { defaults: Map<string, string>; overrides: Map<string, string> } {
    const defaults = new Map<string, string>();
    const overrides = new Map<string, string>();
    const root = this.xml('/[Content_Types].xml');
    for (const def of root.all('Default')) {
      const ext = def.attr('Extension');
      const type = def.attr('ContentType');
      if (ext && type) defaults.set(ext.toLowerCase(), type);
    }
    for (const over of root.all('Override')) {
      const part = over.attr('PartName');
      const type = over.attr('ContentType');
      if (part && type) overrides.set(normalisePart(part), type);
    }
    return { defaults, overrides };
  }
}

export function normalisePart(name: string): string {
  const withSlash = name.startsWith('/') ? name : `/${name}`;
  return withSlash.replace(/\\/g, '/').replace(/\/+/g, '/');
}

export function relsPartFor(partName: string): string {
  const name = normalisePart(partName);
  const slash = name.lastIndexOf('/');
  const dir = name.slice(0, slash);
  const file = name.slice(slash + 1);
  return `${dir}/_rels/${file}.rels`;
}

/** Resolves a relationship target against the source part's directory. */
export function resolveRelative(sourcePart: string, target: string): string {
  if (target.startsWith('/')) return normalisePart(target);
  const source = normalisePart(sourcePart);
  const dir = source.slice(0, source.lastIndexOf('/'));
  const segments = `${dir}/${target}`.split('/');
  const stack: string[] = [];
  for (const segment of segments) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') stack.pop();
    else stack.push(segment);
  }
  return `/${stack.join('/')}`;
}

export function extensionOf(partName: string): string {
  const dot = partName.lastIndexOf('.');
  return dot === -1 ? '' : partName.slice(dot + 1).toLowerCase();
}
